// Pagination — numbered pager with first/last/current ±2 + ellipsis,
// prev/next arrows, and a "Showing X–Y of N" label.
//
// Stateless; consumes the PageInfo envelope returned by the gateway
// alongside any list response. Pair with usePagination()/useTableState()
// to manage the local current page (0-indexed).
//
// Layout: compact on mobile (just prev/next + "X / Y"), expanded on
// desktop (full numbered strip). Two media-controlled <div>s — Tailwind
// only, no JS responsive logic.
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import type { PageInfo } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { intlLocale } from "@/i18n";

const ITEM_LABEL_KEYS = new Set([
  "admins",
  "categories",
  "changes",
  "comments",
  "deposits",
  "disputes",
  "entries",
  "events",
  "keys",
  "leagues",
  "markets",
  "members",
  "movements",
  "orders",
  "overrides",
  "positions",
  "requests",
  "roles",
  "sources",
  "trades",
  "transactions",
  "versions",
  "wallets",
  "withdrawals",
]);

export interface PaginationProps {
  page: PageInfo;
  // 1-indexed for display? No — we keep the 0-indexed convention from
  // usePagination(); the renderer adds +1 only for the visible label.
  // The contract here is "currentPage in [0, totalPages)".
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  // Optional: noun shown next to the count, e.g. "users", "orders".
  // Falls back to "items".
  itemLabel?: string;
}

// Build the page-number window: always show 1, the last page, the
// current page, and ±2 around current. Insert "…" sentinels for gaps.
// Returns a list of numbers (1-indexed) and "…" strings; the caller
// renders accordingly.
function buildWindow(current: number, total: number): (number | "…")[] {
  // Below the threshold we just emit every page — ellipsis on a 6-page
  // dataset reads like a bug.
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const set = new Set<number>([1, total, current]);
  for (let d = 1; d <= 2; d++) {
    if (current - d >= 1) set.add(current - d);
    if (current + d <= total) set.add(current + d);
  }
  const sorted = [...set].sort((a, b) => a - b);

  const out: (number | "…")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    const next = sorted[i + 1];
    if (next !== undefined && next - sorted[i] > 1) out.push("…");
  }
  return out;
}

export function Pagination({
  page,
  currentPage,
  onPageChange,
  className,
  itemLabel,
}: PaginationProps) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage === "th" ? "th" : "en");
  const resolvedItemLabel = itemLabel && ITEM_LABEL_KEYS.has(itemLabel)
    ? t(`common.itemLabels.${itemLabel}`)
    : itemLabel ?? t("common.items");
  const limit = page.limit > 0 ? page.limit : 1;
  const totalPages = Math.max(1, Math.ceil(page.total / limit));
  // Clamp the displayed page into [1, totalPages] in case a stale
  // currentPage outlives a filter change.
  const display = Math.min(Math.max(currentPage, 0), totalPages - 1) + 1;
  const prevDisabled = page.offset <= 0 || currentPage <= 0;
  const nextDisabled = !page.has_more;

  // Showing X–Y of N — compute the slice the table currently shows.
  // page.offset + (rendered count) would be more accurate, but offset
  // + limit captures the upper bound the server returned.
  const fromItem = page.total === 0 ? 0 : page.offset + 1;
  const toItem = Math.min(page.offset + limit, page.total);

  const window = buildWindow(display, totalPages);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-between gap-3 border-t border-border px-5 py-3 sm:flex-row",
        className,
      )}
    >
      <span className="text-2xs text-muted">
        {t("common.showing", {
          from: fromItem.toLocaleString(locale),
          to: toItem.toLocaleString(locale),
          total: page.total.toLocaleString(locale),
          items: resolvedItemLabel,
        })}
      </span>

      {/* Mobile: prev/next + "page X / Y". The numbered strip would
          wrap on narrow screens and look broken; collapse it. */}
      <div className="flex items-center gap-2 sm:hidden">
        <Button
          size="sm"
          variant="ghost"
          disabled={prevDisabled}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label={t("common.previousPage")}
        >
          {"<"}
        </Button>
        <span className="text-2xs text-muted">
          {t("common.pageOf", { page: display, total: totalPages })}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={nextDisabled}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label={t("common.nextPage")}
        >
          {">"}
        </Button>
      </div>

      {/* Desktop: full numbered pager. */}
      <nav
        aria-label={t("common.pagination")}
        className="hidden items-center gap-1 sm:flex"
      >
        <Button
          size="sm"
          variant="ghost"
          disabled={prevDisabled}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label={t("common.previousPage")}
        >
          {"<"}
        </Button>
        {window.map((p, i) =>
          p === "…" ? (
            <span
              key={`gap-${i}`}
              aria-hidden="true"
              className="px-1.5 text-2xs text-muted"
            >
              {"…"}
            </span>
          ) : (
            <button
              key={p}
              type="button"
              aria-current={p === display ? "page" : undefined}
              aria-label={t("common.page", { page: p })}
              onClick={() => onPageChange(p - 1)}
              className={cn(
                "num inline-flex h-7 min-w-[28px] items-center justify-center rounded border px-2 text-2xs font-medium transition-colors",
                p === display
                  ? "border-line-3 bg-neon/10 text-fg"
                  : "border-line-1 bg-transparent text-muted hover:border-line-3 hover:text-fg",
              )}
            >
              {p}
            </button>
          ),
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={nextDisabled}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label={t("common.nextPage")}
        >
          {">"}
        </Button>
      </nav>
    </div>
  );
}
